import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha
} from '@mui/material';
import {
  Phone,
  Email,
  Sms,
  Person,
  Edit,
  DirectionsCar,
  Fingerprint,
  Note,
  Add,
  Delete,
  Share,
  Label,
  Assignment,
  LocationOn,
  Badge as BadgeIcon
} from '@mui/icons-material';
import moment from 'moment';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import constants from '../../utilities/constants';
import { manualStatusOptions, isStatusLocked, statusLabel, soldCaption } from '../../utilities/leadStatus';
import { useSelectMenuProps } from '../../utilities/ModalStackContext';

const SOURCE_OPTIONS = [
  'Autotrader', 'Car show', 'Cars.com', 'Cargurus', 'Classic', 'classicars',
  'Cold call', 'Cold email', 'eBay', 'Facebook', 'Hemmings', 'Walk-in', 'Website', 'Other'
];

const blankForm = (lead) => ({
  name: lead.name || '',
  phone: lead.phone || '',
  email: lead.email || '',
  stock: lead.stock || '',
  id: lead.id || '',
  source: lead.source || '',
  status: lead.status || 'pending contact',
  sales_id: lead.sales_id || ''
});

const ContactHistory = ({ lead, onUpdate }) => {
  const theme = useTheme();
  const selectMenuProps = useSelectMenuProps();
  const contacts = lead.contacts || [];
  const notes = Array.isArray(lead.notes)
    ? lead.notes
    : typeof lead.notes === 'string' && lead.notes.trim() !== ''
      ? [lead.notes]
      : [];
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(blankForm(lead));
  const [updating, setUpdating] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [followUp, setFollowUp] = useState(lead.nextFollowUp || '');

  // Set/clear the next follow-up date (YYYY-MM-DD). Optimistic: write in the
  // background, only surface an alert on failure.
  const saveFollowUp = async (value) => {
    setFollowUp(value);
    lead.nextFollowUp = value || null;
    try {
      await firebase.firestore()
        .collection('leads')
        .doc(lead.id)
        .update({
          nextFollowUp: value || firebase.firestore.FieldValue.delete(),
          updated_at: firebase.firestore.FieldValue.serverTimestamp()
        });
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving follow-up date:', error);
      StateManager.setAlertAndOpen('Error saving follow-up date', 'error');
    }
  };

  const salesRepName = (() => {
    const rep = constants.sales.find(s => s.id === lead.sales_id);
    return rep ? rep.name : 'Unassigned';
  })();

  const getContactIcon = (type) => {
    switch (type) {
      case 'phone':
        return <Phone />;
      case 'email':
        return <Email />;
      case 'text':
        return <Sms />;
      case 'social media':
        return <Share />;
      default:
        return <Person />;
    }
  };

  const getContactColor = (type) => {
    switch (type) {
      case 'phone':
        return 'primary';
      case 'email':
        return 'secondary';
      case 'text':
        return 'info';
      case 'social media':
        return 'default';
      default:
        return 'default';
    }
  };

  // Get status color - matching LeadCard.js
  const getStatusColor = (status) => {
    const statusIndex = constants.lead_statuses.indexOf(status);
    if (statusIndex === -1) return theme.palette.grey[500];

    const colors = [
      '#708090',                   // pending contact
      theme.palette.primary.main,  // qualifying
      theme.palette.success.main,  // closing
      theme.palette.error.main,    // dead
      theme.palette.success.main,  // won
      theme.palette.warning.main   // car sold - other buyer
    ];

    return colors[statusIndex] || theme.palette.grey[500];
  };

  const startEdit = () => {
    setForm(blankForm(lead));
    setEditing(true);
  };

  const cancelEdit = () => {
    setForm(blankForm(lead));
    setEditing(false);
  };

  const setField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Save every changed field in one write. A changed Lead ID (admin only) means
  // creating a new document and deleting the old one, so it's handled last.
  const handleSaveAll = async () => {
    if (!form.name?.trim()) {
      StateManager.setAlertAndOpen('Name is required', 'error');
      return;
    }

    setUpdating(true);
    try {
      const updateData = { updated_at: firebase.firestore.FieldValue.serverTimestamp() };
      const editableFields = ['name', 'phone', 'email', 'stock', 'source'];
      editableFields.forEach(field => {
        if (form[field] !== (lead[field] || '')) {
          updateData[field] = form[field];
          lead[field] = form[field];
        }
      });

      if (!isStatusLocked(lead) && form.status !== (lead.status || 'pending contact')) {
        updateData.status = form.status;
        updateData.status_time = moment().format('YYYY-MM-DD HH:mm:ss');
        lead.status = form.status;
        lead.status_time = updateData.status_time;
      }

      if ((form.sales_id || '') !== (lead.sales_id || '')) {
        updateData.sales_id = form.sales_id || null;
        lead.sales_id = form.sales_id || null;
      }

      // Lead ID change (admin): re-key the document.
      const newId = form.id?.trim();
      const idChanged = newId && newId !== lead.id;
      if (idChanged) {
        const oldId = lead.id;
        const leadData = { ...lead };
        delete leadData.id;
        await firebase.firestore().collection('leads').doc(newId).set({
          ...leadData,
          ...updateData
        });
        await firebase.firestore().collection('leads').doc(oldId).delete();
        lead.id = newId;
      } else {
        await firebase.firestore().collection('leads').doc(lead.id).update(updateData);
      }

      StateManager.setAlertAndOpen('Lead updated successfully', 'success');
      if (onUpdate) onUpdate();
      setEditing(false);
    } catch (error) {
      console.error('Error updating lead:', error);
      StateManager.setAlertAndOpen('Error updating lead', 'error');
    } finally {
      setUpdating(false);
    }
  };

  // Note handling functions
  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setUpdating(true);
    try {
      const note = {
        id: Date.now().toString(),
        text: newNote.trim(),
        timestamp: moment().format('YYYY/MM/DD HH:mm'),
        user: StateManager.userName || 'Unknown'
      };

      const updatedNotes = [...notes, note];

      await firebase.firestore()
        .collection('leads')
        .doc(lead.id)
        .update({
          notes: updatedNotes,
          updated_at: firebase.firestore.FieldValue.serverTimestamp()
        });

      // Update the local lead object
      lead.notes = updatedNotes;

      setNewNote('');
      setAddingNote(false);
      
      StateManager.setAlertAndOpen("Note added successfully", "success");
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error adding note:', error);
      StateManager.setAlertAndOpen("Error adding note", "error");
    } finally {
      setUpdating(false);
    }
  };


  const handleDeleteNote = async (noteId) => {
    setUpdating(true);
    try {
      const updatedNotes = notes.filter(note => note.id !== noteId);

      await firebase.firestore()
        .collection('leads')
        .doc(lead.id)
        .update({
          notes: updatedNotes,
          updated_at: firebase.firestore.FieldValue.serverTimestamp()
        });

      // Update the local lead object
      lead.notes = updatedNotes;
      
      StateManager.setAlertAndOpen("Note deleted successfully", "success");
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      StateManager.setAlertAndOpen("Error deleting note", "error");
    } finally {
      setUpdating(false);
    }
  };


  // Quick log of contact attempts (phone, email, sms)
  const logContactAttempt = async (type) => {
    setUpdating(true);
    try {
      const newContact = {
        type,
        timestamp: moment().format('YYYY/MM/DD HH:mm'),
        note: `Contact attempt via ${type}`,
        user: StateManager.userName || 'Unknown'
      };

      const prevContacts = lead.contacts || [];
      const updatedContacts = [...prevContacts, newContact];

      // Denormalized contact timestamps for cheap "last/first contacted" reads.
      // NOTE: the backend addContact (skyway-functions) must mirror these to stay
      // accurate for automation-logged (SMS/email) contacts.
      const update = {
        contacts: updatedContacts,
        lastContactedAt: newContact.timestamp,
        lastContactType: newContact.type,
        updated_at: firebase.firestore.FieldValue.serverTimestamp()
      };
      if (prevContacts.length === 0) update.firstContactedAt = newContact.timestamp;

      await firebase.firestore()
        .collection('leads')
        .doc(lead.id)
        .update(update);

      // Update the local lead object
      lead.contacts = updatedContacts;
      lead.lastContactedAt = newContact.timestamp;
      lead.lastContactType = newContact.type;
      if (prevContacts.length === 0) lead.firstContactedAt = newContact.timestamp;

      if (onUpdate) onUpdate();
      StateManager.setAlertAndOpen('Contact logged successfully', 'success');
    } catch (error) {
      console.error('Error logging contact:', error);
      StateManager.setAlertAndOpen('Error logging contact', 'error');
    } finally {
      setUpdating(false);
    }
  };


  const handleDeleteContact = async (contactToDelete) => {
    setUpdating(true);
    try {
      const contactsArray = Array.isArray(lead.contacts) ? [...lead.contacts] : [];
      const indexToRemove = contactsArray.findIndex(c => (
        c.type === contactToDelete.type &&
        c.timestamp === contactToDelete.timestamp &&
        c.note === contactToDelete.note &&
        c.user === contactToDelete.user
      ));

      if (indexToRemove === -1) {
        setUpdating(false);
        return;
      }

      contactsArray.splice(indexToRemove, 1);

      await firebase.firestore()
        .collection('leads')
        .doc(lead.id)
        .update({
          contacts: contactsArray,
          updated_at: firebase.firestore.FieldValue.serverTimestamp()
        });

      // Update the local lead object
      lead.contacts = contactsArray;

      StateManager.setAlertAndOpen('Contact deleted successfully', 'success');

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting contact:', error);
      StateManager.setAlertAndOpen('Error deleting contact', 'error');
    } finally {
      setUpdating(false);
    }
  };


  const statusColor = getStatusColor(lead.status || 'pending contact');

  const renderInfoRow = (icon, label, value) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
      {icon}
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {value || <span style={{ color: theme.palette.text.disabled }}>—</span>}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* Lead Information Section */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Lead Information</Typography>
          {!editing && (
            <Button variant="outlined" size="small" startIcon={<Edit />} onClick={startEdit} disabled={updating}>
              Edit
            </Button>
          )}
        </Box>

        {editing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              size="small" label="Name" value={form.name}
              onChange={(e) => setField('name', e.target.value)} disabled={updating} fullWidth
            />
            <TextField
              size="small" label="Phone" value={form.phone}
              onChange={(e) => setField('phone', e.target.value)} disabled={updating} fullWidth
            />
            <TextField
              size="small" label="Email" value={form.email}
              onChange={(e) => setField('email', e.target.value)} disabled={updating} fullWidth
            />
            <TextField
              size="small" label="Stock Number" value={form.stock}
              onChange={(e) => setField('stock', e.target.value)} disabled={updating} fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status} label="Status" disabled={updating || isStatusLocked(lead)}
                MenuProps={selectMenuProps}
                onChange={(e) => setField('status', e.target.value)}
              >
                {/* won is system-set (funded deal): shown, never offered */}
                {isStatusLocked(lead) && (
                  <MenuItem value={form.status} disabled>{soldCaption(lead)}</MenuItem>
                )}
                {manualStatusOptions().map((status) => (
                  <MenuItem key={status} value={status}>
                    {statusLabel(status)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Sales Rep</InputLabel>
              <Select
                value={form.sales_id} label="Sales Rep" disabled={updating}
                MenuProps={selectMenuProps}
                onChange={(e) => setField('sales_id', e.target.value)}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {constants.sales.map((rep) => (
                  <MenuItem key={rep.id} value={rep.id}>{rep.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {StateManager.isAdmin() && (
              <FormControl size="small" fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={form.source} label="Source" disabled={updating}
                  MenuProps={selectMenuProps}
                  onChange={(e) => setField('source', e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {SOURCE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option === 'classicars' ? 'ClassicCars.com' : option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {StateManager.isAdmin() && (
              <TextField
                size="small" label="Lead ID" value={form.id}
                onChange={(e) => setField('id', e.target.value)} disabled={updating} fullWidth
                helperText="Changing the ID re-keys the lead document."
              />
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={cancelEdit} disabled={updating}>Cancel</Button>
              <Button size="small" variant="contained" onClick={handleSaveAll} disabled={updating}>
                Save Changes
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {renderInfoRow(<Person fontSize="small" />, 'Name', lead.name)}
            {renderInfoRow(<Phone fontSize="small" />, 'Phone', lead.phone)}
            {renderInfoRow(<Email fontSize="small" />, 'Email', lead.email)}
            {lead.zipCode && renderInfoRow(<LocationOn fontSize="small" />, 'Zip', lead.zipCode)}
            {renderInfoRow(<DirectionsCar fontSize="small" />, 'Stock', lead.stock)}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Assignment fontSize="small" />
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Status</Typography>
              <Chip
                label={isStatusLocked(lead) ? soldCaption(lead) : statusLabel(lead.status)}
                size="small"
                sx={{
                  bgcolor: alpha(statusColor, 0.1),
                  color: statusColor,
                  border: `1px solid ${alpha(statusColor, 0.3)}`,
                }}
              />
            </Box>
            {renderInfoRow(<BadgeIcon fontSize="small" />, 'Sales Rep', salesRepName)}
            {renderInfoRow(<Label fontSize="small" />, 'Source', lead.source)}
            {StateManager.isAdmin() && renderInfoRow(<Fingerprint fontSize="small" />, 'Lead ID', lead.id)}
          </>
        )}
      </Paper>

      {/* Notes Section */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Notes ({notes.length})
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Add />}
            onClick={() => setAddingNote(true)}
            disabled={updating}
          >
            Add Note
          </Button>
        </Box>

        {/* Add Note Form */}
        {addingNote && (
          <Card sx={{ mb: 2, bgcolor: 'grey.50' }}>
            <CardContent>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter your note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                disabled={updating}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  onClick={() => {
                    setAddingNote(false);
                    setNewNote('');
                  }}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAddNote}
                  disabled={!newNote.trim() || updating}
                >
                  Add Note
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Notes List */}
        {notes.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notes yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {notes
              .sort((a, b) => moment(b.timestamp, 'YYYY/MM/DD HH:mm').valueOf() - moment(a.timestamp, 'YYYY/MM/DD HH:mm').valueOf())
              .map((note, index) => (
                <React.Fragment key={note.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: 'primary.main',
                          color: 'white'
                        }}
                      >
                        <Note />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {note.timestamp}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            by {note.user}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2">
                          {note.text || note.value}
                        </Typography>
                      }
                    />
                    <Box sx={{ ml: 1 }}>
                      <Tooltip title="Delete note">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteNote(note.id)}
                          disabled={updating}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  {index < notes.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              ))}
          </List>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assignment fontSize="small" color="action" />
          <Typography variant="subtitle2">Next follow-up</Typography>
        </Box>
        <TextField
          type="date"
          size="small"
          value={followUp}
          onChange={(e) => saveFollowUp(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />
        {followUp && (
          <>
            <Chip
              size="small"
              color={moment(followUp, 'YYYY-MM-DD').isBefore(moment(), 'day') ? 'error' : 'info'}
              label={
                moment(followUp, 'YYYY-MM-DD').isBefore(moment(), 'day')
                  ? `Overdue — ${moment(followUp, 'YYYY-MM-DD').fromNow()}`
                  : `Due ${moment(followUp, 'YYYY-MM-DD').fromNow()}`
              }
            />
            <Button size="small" color="inherit" onClick={() => saveFollowUp('')}>
              Clear
            </Button>
          </>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Contact History ({contacts.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={lead.phone ? `Log phone contact` : 'No phone'}>
            <span>
              <IconButton size="small" onClick={() => logContactAttempt('phone')} disabled={updating || !lead.phone}>
                <Phone fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={lead.email ? `Log email contact` : 'No email'}>
            <span>
              <IconButton size="small" onClick={() => logContactAttempt('email')} disabled={updating || !lead.email}>
                <Email fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={lead.phone ? `Log SMS contact` : 'No phone'}>
            <span>
              <IconButton size="small" onClick={() => logContactAttempt('text')} disabled={updating || !lead.phone}>
                <Sms fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Log social media contact">
            <span>
              <IconButton size="small" onClick={() => logContactAttempt('social media')} disabled={updating}>
                <Share fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      
      {contacts.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No contact history yet
          </Typography>
        </Box>
      ) : (
        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {contacts
            .sort((a, b) => moment(b.timestamp, 'YYYY/MM/DD HH:mm').valueOf() - moment(a.timestamp, 'YYYY/MM/DD HH:mm').valueOf())
            .map((contact, index) => (
              <React.Fragment key={index}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: `${getContactColor(contact.type)}.main`,
                        color: 'white'
                      }}
                    >
                      {getContactIcon(contact.type)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip
                          label={contact.type === 'text' ? 'SMS' : contact.type === 'social media' ? 'Social Media' : contact.type.charAt(0).toUpperCase() + contact.type.slice(1)}
                          size="small"
                          color={getContactColor(contact.type)}
                          variant="outlined"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {contact.timestamp}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {contact.note}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          by {contact.user}
                        </Typography>
                      </Box>
                    }
                  />
                  {StateManager.isAdmin() && (
                    <Box sx={{ ml: 1 }}>
                      <Tooltip title="Delete contact">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteContact(contact)}
                          disabled={updating}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </ListItem>
                {index < contacts.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            ))}
        </List>
      )}
    </Box>
  );
};

export default ContactHistory;

